import { useEffect, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';

/**
 * Shoutout — v2 port of v1's shoutout.ejs + ShoutoutSettings.
 * Auto-triggers on raid events. Renders an avatar + name + message card
 * for a configurable duration, then hides.
 *
 * Config (all optional):
 *   displayDuration: seconds                    (default 8)
 *   showAvatar: boolean                         (default true)
 *   showGame: boolean                           (default true) — requires avatarLookupUrl
 *   accentColor: string                         (default '#8b5cf6')
 *   bgStyle: 'glass' | 'solid' | 'minimal'      (default 'glass')
 *   messageTemplate: string                     (default 'Check out {username}! Go show them some love!')
 *   raidMessageTemplate: string                 (default '{username} raided with {viewers} viewers!')
 *   triggerOnRaid: boolean                      (default true)
 *   avatarLookupUrl: string                     fetched as `${url}?platform=X&user=Y` -> { avatar, game }
 */

interface ShoutoutData {
  id: string;
  username: string;
  source: 'raid' | 'manual';
  viewers: number;
}

interface AvatarInfo {
  avatar: string | null;
  game: string | null;
}

function lightenHex(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, (num >> 16) + Math.round(2.55 * percent));
  const g = Math.min(255, ((num >> 8) & 0xff) + Math.round(2.55 * percent));
  const b = Math.min(255, (num & 0xff) + Math.round(2.55 * percent));
  return '#' + (0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

function render(tpl: string, vars: Record<string, string>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? '');
}

export function ShoutoutWidget({
  fields,
  latestEvent,
}: {
  fields: Record<string, unknown>;
  latestEvent: StreamEvent | null;
}) {
  const duration = Number(fields.displayDuration ?? 8) * 1000;
  const showAvatar = (fields.showAvatar as boolean | undefined) ?? true;
  const showGame = (fields.showGame as boolean | undefined) ?? true;
  const accent = String(fields.accentColor ?? '#8b5cf6');
  const bgStyle = (fields.bgStyle as 'glass' | 'solid' | 'minimal' | undefined) ?? 'glass';
  const triggerOnRaid = (fields.triggerOnRaid as boolean | undefined) ?? true;
  const messageTemplate = String(
    fields.messageTemplate ?? 'Check out {username}! Go show them some love!',
  );
  const raidMessageTemplate = String(
    fields.raidMessageTemplate ?? '{username} raided with {viewers} viewers!',
  );
  const avatarLookupUrl = (fields.avatarLookupUrl as string | undefined) ?? null;

  const [current, setCurrent] = useState<ShoutoutData | null>(null);
  const [avatar, setAvatar] = useState<AvatarInfo | null>(null);
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const queue = useRef<ShoutoutData[]>([]);
  const lastId = useRef<string | null>(null);
  const showing = useRef(false);

  // Enqueue on raid
  useEffect(() => {
    if (!latestEvent || latestEvent.id === lastId.current) return;
    lastId.current = latestEvent.id;
    if (!triggerOnRaid || latestEvent.type !== 'raid') return;
    queue.current.push({
      id: latestEvent.id,
      username: latestEvent.raiderName,
      source: 'raid',
      viewers: latestEvent.viewers,
    });
    pump();
  }, [latestEvent, triggerOnRaid]);

  function pump() {
    if (showing.current || queue.current.length === 0) return;
    showing.current = true;
    const next = queue.current.shift()!;
    setCurrent(next);
    setAvatar(null);
    setPhase('in');

    // Fetch avatar / game in background if configured
    if (avatarLookupUrl && (showAvatar || showGame)) {
      void fetch(`${avatarLookupUrl}?platform=twitch&user=${encodeURIComponent(next.username)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data: AvatarInfo | null) => {
          if (data) setAvatar(data);
        })
        .catch(() => undefined);
    }

    setTimeout(() => setPhase('out'), duration - 500);
    setTimeout(() => {
      setCurrent(null);
      showing.current = false;
      pump();
    }, duration);
  }

  if (!current) return null;

  const isRaid = current.source === 'raid';
  const label = isRaid ? 'RAID' : 'SHOUTOUT';
  const message = render(isRaid ? raidMessageTemplate : messageTemplate, {
    username: current.username,
    viewers: String(current.viewers),
  });

  const bg =
    bgStyle === 'solid'
      ? 'linear-gradient(135deg,#0a0818,#120e26)'
      : bgStyle === 'minimal'
        ? 'rgba(10,8,24,0.6)'
        : 'linear-gradient(135deg, rgba(15,12,30,0.92), rgba(22,16,46,0.92))';

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        fontFamily: "'Poppins', system-ui, sans-serif",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: phase === 'in' ? 1 : 0,
        transform: phase === 'in' ? 'translateY(0)' : 'translateY(20px)',
        transition:
          'opacity 500ms cubic-bezier(0.22,1,0.36,1), transform 500ms cubic-bezier(0.22,1,0.36,1)',
      }}
    >
      <div
        style={{
          minWidth: 360,
          maxWidth: 520,
          background: bg,
          border: `1px solid ${accent}40`,
          borderRadius: 14,
          padding: '14px 18px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          boxShadow: `0 8px 32px rgba(0,0,0,0.55), 0 0 24px ${accent}20`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 24,
            right: 24,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
            opacity: 0.6,
          }}
        />
        {showAvatar && (
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${accent}, ${lightenHex(accent, 30)})`,
              border: `2px solid ${accent}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              fontWeight: 800,
              flexShrink: 0,
              overflow: 'hidden',
              boxShadow: `0 0 16px ${accent}40`,
            }}
          >
            {avatar?.avatar ? (
              <img
                src={avatar.avatar}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
              />
            ) : (
              (current.username[0]?.toUpperCase() ?? '?')
            )}
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: 1.6,
              color: accent,
              textTransform: 'uppercase',
              marginBottom: 2,
            }}
          >
            {label}
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 900,
              letterSpacing: -0.3,
              lineHeight: 1.15,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {current.username}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.7)',
              marginTop: 3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {message}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {isRaid && current.viewers > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: accent,
                  background: `${accent}1f`,
                  borderRadius: 4,
                  padding: '2px 7px',
                }}
              >
                ⚡ {current.viewers.toLocaleString()} viewer{current.viewers === 1 ? '' : 's'}
              </span>
            )}
            {showGame && avatar?.game && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.6)',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: 4,
                  padding: '2px 7px',
                }}
              >
                🎮 {avatar.game}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
